import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { getCommonAreaSquareFeet, getTotalSquareFeetForExpense } from "../utils/operatingExpenseCalc";
import { formatPSF } from "../utils/formatters";

type PdfSummaryProps = {
  modelDetails: any;
  variables?: Record<string, any>;
  logoSrc?: string;
  options?: Record<string, boolean>;
  holdMonthsExternal?: number | null;
  pictures?: Array<{ picture_url: string; description?: string }>;
  order?: string[];
  companyInfo?: {
    company_name?: string;
    company_email?: string;
    company_phone_number?: string;
    company_logo_url?: string;
  } | null;
  summaryTables?: any[];
  otherTables?: any[];
  irrTable?: { capRates: (number | string)[]; acquisitionPrices: (number | string)[]; values: (number | string)[][] };
  moicTable?: { capRates: (number | string)[]; acquisitionPrices: (number | string)[]; values: (number | string)[][] };
  notes?: string[];
  retailMode?: boolean;
  spaceType?: string;
};

/** Vertical breathing room above every section header (matches the gap between the report title area and the first section on page 1). */
const SECTION_TOP_GAP = 16;

/**
 * Max row count for which a table (or a table+title section) is rendered as one
 * non-splittable `wrap={false}` block. react-pdf cannot paginate a `wrap={false}`
 * block: if its real rendered height (which grows when a cell's text wraps to
 * multiple lines) ends up taller than a page, the overflow is silently clipped
 * instead of flowing onto the next page. 18 was too high a bound for that
 * guarantee to hold once cell text wraps (bug: sections cut off at the bottom of
 * the page). 8 rows keeps a safe margin even under multi-line wrapping.
 */
const MAX_ROWS_TO_KEEP_TOGETHER = 8;

/**
 * Zero-height first child for every View that is allowed to split across pages.
 * react-pdf's page splitter (@react-pdf/layout `splitNodes`) has a fallback: when a
 * wrapper's first child has to move to the next page (e.g. a `wrap={false}` title
 * block that doesn't fit), the wrapper looks empty on the current page, and if the
 * wrapper's own parent has nothing before it either, the whole wrapper is placed
 * unsplit on the current page. Yoga then squeezes every row into the space left at
 * the bottom of that page, so a long rent roll draws as one overlapping smear
 * (bug: pdf-rent-roll-collapse). With this anchor on the page, no wrapper is ever
 * empty, so react-pdf moves the content to the next page as intended.
 */
const SplitAnchor = () => <View style={{ height: 0 }} />;

// Base styles for the PDF document
const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 64,
    paddingHorizontal: 32,
    fontSize: 11,
    color: "#1f2937", // slate-800
  },
  pageNumber: {
    position: "absolute",
    bottom: 24,
    right: 34,
    textAlign: "right",
    fontSize: 10,
    color: "#9CA3AF",
  },
  footerLeft: {
    position: "absolute",
    bottom: 24,
    left: 34,
    textAlign: "left",
    fontSize: 10,
    color: "#9CA3AF",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: SECTION_TOP_GAP,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    borderBottomStyle: "solid",
    paddingBottom: 2,
  },
  headerLeft: {
    width: "70%",
    paddingRight: 12,
    marginTop:36
  },
  headerRight: {
    width: 200, // fixed column so right edge is consistent with page padding/divider
    alignItems: "flex-end",
    paddingRight: 0,
    marginRight: 0,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
  },
  subTitle: {
    fontSize: 12,
    marginTop: 1,
    color: "#4B5563",
  },
  logo: {
    height: 40,
    objectFit: "contain",
    alignSelf: "flex-end",
    marginRight: 0,
  },
  companyBlock: {
    alignItems: "flex-end",
  },
  logoWrap: {
    width: "100%",
    alignItems: "flex-end",
    marginBottom: 4,
    marginRight: 0
  },
  companyLine: {
    fontSize: 9,
    color: "#4B5563",
    textAlign: "right",
  },
  section: {
    paddingTop: SECTION_TOP_GAP,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 6,
    paddingBottom: 2,
    textTransform: "uppercase",
    borderBottom: "1.2px solid #E5E7EB",
    minPresenceAhead: 140,
  },
  grid: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  gridItem: {
    width: "50%",
    paddingRight: 12,
    paddingBottom: 8,
  },
  gridItemFull: {
    width: "100%",
    paddingRight: 12,
    paddingBottom: 8,
  },
  gridItemThird: {
    width: "33.3333%",
    paddingRight: 12,
    paddingBottom: 8,
  },
  label: {
    color: "#6B7280",
    fontSize: 10,
  },
  value: {
    fontSize: 11,
    marginTop: 2,
    fontWeight: 700,
  },
});

// Minimal, printable PDF document. Extend sections as needed.
export const PdfSummaryDocument: React.FC<PdfSummaryProps> = ({
  modelDetails,
  variables = {},
  logoSrc,
  options = {},
  holdMonthsExternal,
  pictures = [],
  order = [],
  companyInfo = null,
  summaryTables = [],
  otherTables = [],
  irrTable,
  moicTable,
  notes = [],
  retailMode = false,
  spaceType,
}) => {
  const addressLine = modelDetails
    ? `${modelDetails?.street_address ?? ""}, ${modelDetails?.city ?? ""}, ${
        modelDetails?.state ?? ""
      } ${modelDetails?.zip_code ?? ""}`
    : "";

  const safeVar = (k: string) => (variables && variables[k] !== undefined ? String(variables[k]) : "-");

  // Basic helper to parse numbers from mixed strings (e.g., "10", "10%")
  const toNum = (v: any): number | null => {
    if (v === null || v === undefined) return null;
    const n = Number(String(v).replace(/[^0-9.+-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  // Match ModelDetails.tsx Hold Period logic: use max of Multifamily/Retail Exit Month only
  const computedHoldMonths = (() => {
    const mf = toNum((variables as any)["Multifamily Exit Month"]);
    const rt = toNum((variables as any)["Retail Exit Month"]);
    if (mf === null && rt === null) return null;
    const max = Math.max(mf ?? 0, rt ?? 0);
    return Number.isFinite(max) ? max : null;
  })();
  const holdMonths = holdMonthsExternal ?? computedHoldMonths;

  const formatKpiValue = (val: any, type: 'irr' | 'moic' | 'auto' = 'auto') => {
    if (val === null || val === undefined || val === "" || val === "N/A" || val === "-") return "-";
    const str = String(val).trim();

    // First try to extract a numeric value
    const num = parseFloat(str.replace(/[%,x$]/g, "").replace(/,/g, ""));
    if (isNaN(num)) return str;

    if (type === 'irr' || (type === 'auto' && str.endsWith('%'))) {
      return `${num.toFixed(1)}%`;
    }
    if (type === 'moic') {
      return `${num.toFixed(2)}x`;
    }

    return str;
  };

  // Table utilities (modeled after renderTableMappingRows) for trimming and rendering tables
  const isBlank = (cell: any) =>
    cell === null || cell === undefined || (typeof cell === "string" && cell.trim() === "");

  // PDF page sizing helpers (LETTER width 612pt, horizontal padding 32*2 = 64)
  const CONTENT_WIDTH = 612 - 64; // 548
  const IMG_GUTTER = 8;
  const FULL_IMG_WIDTH = CONTENT_WIDTH;
  // Two cells per row, so 2 * HALF_CELL_WIDTH must stay within CONTENT_WIDTH.
  // The image is capped at the cell's CONTENT box, not the cell width:
  // capping at the cell width let a wide image bleed a gutter's worth past
  // its cell, and in the right-hand column that ran toward the page edge.
  const HALF_CELL_WIDTH = Math.floor((CONTENT_WIDTH - IMG_GUTTER) / 2);
  const HALF_IMG_WIDTH = HALF_CELL_WIDTH - IMG_GUTTER;

  const buildTrimmedTable = (mapping: any) => {
    const data: any[][] = Array.isArray(mapping?.data) ? mapping.data : [];
    const stylesArr: any[][] = Array.isArray(mapping?.styles) ? mapping.styles : [];
    let lastNonBlank = -1;
    for (let i = data.length - 1; i >= 0; i--) {
      const row = data[i] || [];
      if (row.some((cell) => !isBlank(cell))) {
        lastNonBlank = i;
        break;
      }
    }
    if (lastNonBlank === -1) return { rows: [] as any[][], styles: [] as any[][] };
    // Filter out phantom section-header rows: rows where only the first cell
    // has content and every data column is blank (e.g. an empty "Operating Expenses"
    // divider that exists in the sheet but carries no values).
    const allRowIndices: number[] = Array.from({ length: lastNonBlank + 1 }, (_, i) => i);
    const keptRowIndices: number[] = allRowIndices.filter((ri) => {
      const row = data[ri] || [];
      // Always keep the header row (index 0)
      if (ri === 0) return true;
      const firstCell = row[0];
      const hasLabel = !isBlank(firstCell);
      const hasAnyValue = row.slice(1).some((cell) => !isBlank(cell));
      // Drop rows that are label-only (no data in any value column)
      if (hasLabel && !hasAnyValue) return false;
      return true;
    });
    const maxCols = keptRowIndices.reduce((m, ri) => Math.max(m, (data[ri] || []).length), 0);
    const keptColIndices: number[] = [];
    for (let c = 0; c < maxCols; c++) {
      let keep = false;
      for (const ri of keptRowIndices) {
        const cell = (data[ri] || [])[c];
        if (!isBlank(cell)) {
          keep = true;
          break;
        }
      }
      if (keep) keptColIndices.push(c);
    }
    const rows = keptRowIndices.map((ri) => keptColIndices.map((ci) => (data[ri] || [])[ci] ?? ""));
    const styles = keptRowIndices.map((ri) => keptColIndices.map((ci) => (stylesArr[ri] || [])[ci] ?? ""));
    return { rows, styles };
  };

  const tableStyles = StyleSheet.create({
    container: {
      marginTop: 6,
    },
    table: {
      width: "100%",
      marginTop: 6,
    },
    tableRow: { flexDirection: "row", flexShrink: 0 },
    tableCell: {
      borderBottomWidth: 1,
      borderBottomColor: "#e5e7eb",
      borderBottomStyle: "solid",
      paddingVertical: 6,
      paddingRight: 8,
      paddingLeft: 8,
      fontSize: 8,
      flexShrink: 0,
    },
    tableHeaderCell: { fontWeight: 700 },
    tableTotalCell: { fontWeight: 700 },
    right: { textAlign: "right" as const },
  });

  // Map CSS-like inline style strings to react-pdf supported styles
  const mapCssToPdfStyle = (styleStr: string): { cell: any; text: any } => {
    if (!styleStr || typeof styleStr !== "string") return { cell: {}, text: {} };
    const outCell: any = {};
    const outText: any = {};
    const parts = styleStr
      .split(";")
      .map((p) => p.trim())
      .filter(Boolean);
    for (const part of parts) {
      const [rawK, rawV] = part.split(":");
      if (!rawK || !rawV) continue;
      const k = rawK.trim().toLowerCase();
      const v = rawV.trim();
      if (k === "background-color") outCell.backgroundColor = v;
      else if (k === "color") outText.color = v;
      else if (k === "font-weight") outText.fontWeight = v === "bold" ? 700 : Number(v) || undefined;
      else if (k === "text-align") outText.textAlign = v as any;
      else if (k === "border-bottom") {
        // expect like: 1px solid #e0e0e0
        const m = v.match(/(\d+)px\s+\w+\s+(.*)/);
        outCell.borderBottomWidth = m ? Number(m[1]) : 1;
        outCell.borderBottomStyle = "solid";
        outCell.borderBottomColor = m ? m[2] : "#e5e7eb";
      }
    }
    return { cell: outCell, text: outText };
  };

  const looksNumeric = (val: any): boolean => {
    const s = String(val ?? "").trim();
    if (!s) return false;
    return /[0-9]/.test(s) && !/[a-zA-Z]/.test(s);
  };

  /** Returns true when the given mapping would produce a small table that stays on one page. */
  const isSmallTable = (mapping: any): boolean => {
    const { rows } = buildTrimmedTable(mapping);
    return rows.length > 0 && rows.length <= MAX_ROWS_TO_KEEP_TOGETHER;
  };

  const renderPdfTable = (mapping: any, key: string, sectionTitle?: string, opts?: { disableWrapFalse?: boolean }) => {
    const { rows: rawRows, styles: rawStyles } = buildTrimmedTable(mapping);
    if (!rawRows || rawRows.length === 0) return null;

    const rows: any[][] = rawRows;
    const cellStyles: any[][] = rawStyles;

    // Rename post-vacancy "Gross Potential Income" → "Effective Gross Income" (JIRA-334)
    let sawVacancy = false;
    for (let ri = 0; ri < rows.length; ri++) {
      const label = String(rows[ri]?.[0] ?? "").trim().toLowerCase();
      if (label.startsWith("less:") && label.includes("vacancy")) {
        sawVacancy = true;
      } else if (sawVacancy && label === "gross potential income") {
        rows[ri][0] = "Effective Gross Income";
        sawVacancy = false;
      } else if (!label.startsWith("less:")) {
        sawVacancy = false;
      }
    }

    const colCount = rows.reduce((m, r) => Math.max(m, r.length), 0);
    const base = colCount > 0 ? 100 / (colCount + 1) : 100; // first column counts as 2x
    const firstPct = (2 * base).toFixed(6);
    const otherPct = base.toFixed(6);
    // Small tables should stay together; large tables can paginate by row
    // When disableWrapFalse is set, the caller manages wrap on a parent container
    const keepTogether = rows.length <= MAX_ROWS_TO_KEEP_TOGETHER && !opts?.disableWrapFalse;
    const containerProps: any = keepTogether ? { wrap: false } : {};

    // Helper: render a single table row
    const renderTableRow = (row: any[], ri: number, rowKey: string) => {
      const rowLabel = String(row[0] ?? "").trim();
      const isTotalRow = ri > 0 && /^Total\s+(NOI|Sources|Uses|Property NOI)/i.test(rowLabel);
      const spacerLabels = ["length of deal", "unlevered moic", "levered moic"];
      const needsSpacer = ri > 0 && spacerLabels.includes(rowLabel.toLowerCase());
      const rowStyle = needsSpacer
        ? [tableStyles.tableRow, { marginBottom: 8 }]
        : tableStyles.tableRow;
      return (
        <View key={rowKey} style={rowStyle} wrap={false}>
          {Array.from({ length: colCount }).map((_, ci) => {
            const raw = row[ci];
            const cellStyleStr = (cellStyles[ri] && cellStyles[ri][ci]) || "";
            const mapped = mapCssToPdfStyle(cellStyleStr);
            const isFirst = ci === 0;
            const finalCellStyle = [
              tableStyles.tableCell,
              ri === 0 ? tableStyles.tableHeaderCell : undefined,
              isTotalRow ? { borderTopWidth: 2, borderTopColor: "#1a2b4a", borderTopStyle: "solid" as const } : undefined,
              mapped.cell,
              { width: `${isFirst ? firstPct : otherPct}%` },
            ] as any;
            const finalTextStyle = [
              mapped.text,
              isTotalRow ? { fontWeight: 700 } : undefined,
              !isFirst ? tableStyles.right : undefined,
            ] as any;
            return (
              <View key={`c-${ci}`} style={finalCellStyle}>
                <Text style={finalTextStyle}>
                  {(() => {
                      if (raw === undefined || raw === null || String(raw).trim() === "") return " ";
                      const str = String(raw).trim();
                      const sheetErrors = new Set(["#DIV/0!", "#REF!", "#N/A", "#VALUE!", "#NAME?", "#NULL!", "#ERROR!", "#CALC!"]);
                      if (sheetErrors.has(str)) return "\u2014";
                      const rowLabel = String(row[0] || "").toLowerCase();
                      if (rowLabel.includes("irr") || str.endsWith("%")) {
                        return formatKpiValue(str, 'irr');
                      }
                      if (rowLabel.includes("moic")) {
                        return formatKpiValue(str, 'moic');
                      }
                      return str;
                    })()}
                </Text>
              </View>
            );
          })}
        </View>
      );
    };

    if (keepTogether) {
      return (
        <View key={key} style={tableStyles.container} {...containerProps}>
          <View style={tableStyles.table}>
            {rows.map((row, ri) => renderTableRow(row, ri, `r-${ri}`))}
          </View>
        </View>
      );
    }
    // When disableWrapFalse is set, the caller manages the outer container (including title).
    // Otherwise render the title inside our own orphan-protected intro block.
    const renderOwnTitle = !!sectionTitle && !opts?.disableWrapFalse;
    return (
      <View key={key} style={tableStyles.container}>
        <SplitAnchor />
        {/* Single table wrapper is required: cell widths are `%` of the parent row,
            and the row's width needs to resolve against a 100%-width container.
            Without this wrapper rows can collapse/overlap (observed on multi-page
            tables like NOI Walk). */}
        <View style={tableStyles.table}>
          <SplitAnchor />
          {/* Keep [title, header row, first data row] together so the title never orphans. */}
          <View wrap={false}>
            {renderOwnTitle ? <Text style={styles.sectionTitle}>{sectionTitle}</Text> : null}
            {renderTableRow(rows[0], 0, "r-0")}
            {rows[1] ? renderTableRow(rows[1], 1, "r-1") : null}
          </View>
          {rows.slice(2).map((row, i) => renderTableRow(row, i + 2, `r-${i + 2}`))}
        </View>
      </View>
    );
  };

  // Sensitivity table rendering (IRR/MOIC)
  const renderSensitivity = (
    tbl: { capRates: (number | string)[]; acquisitionPrices: (number | string)[]; values: (number | string)[][] } | undefined,
    key: string,
    format: (n: number) => string,
    leftHeader: string
  ) => {
    if (!tbl || !Array.isArray(tbl.capRates) || !Array.isArray(tbl.acquisitionPrices)) return null;
    const headerRow = [leftHeader, ...tbl.acquisitionPrices.map((p) => `$${(toNum(p) ?? 0).toLocaleString()}`)];
    const rows = [headerRow, ...tbl.capRates.map((cr, ri) => {
      const left = `${(toNum(cr) ?? 0).toFixed(2)}%`;
      const vals = (tbl.values?.[ri] || []).map((v) => format(toNum(v) ?? 0));
      return [left, ...vals];
    })];
    const colCount = rows[0]?.length || 0;
    const base = colCount > 0 ? 100 / (colCount + 1) : 100; // 2x first col
    const firstPct = (2 * base).toFixed(6);
    const otherPct = base.toFixed(6);
    return (
      <View key={key} style={tableStyles.container}>
        <View style={tableStyles.table}>
          {rows.map((row, ri) => (
            <View key={`sr-${ri}`} style={tableStyles.tableRow} wrap={false}>
              {row.map((raw, ci) => {
                const isFirst = ci === 0;
                const finalCellStyle = [
                  tableStyles.tableCell,
                  ri === 0 ? tableStyles.tableHeaderCell : undefined,
                  { width: `${isFirst ? firstPct : otherPct}%` },
                ] as any;
                const finalTextStyle = [!isFirst ? tableStyles.right : undefined] as any;
                return (
                  <View key={`sc-${ci}`} style={finalCellStyle}>
                    <Text style={finalTextStyle}>{String(raw)}</Text>
                  </View>
                );
              })}
            </View>
          ))}
        </View>
      </View>
    );
  };

  // Generic simple table renderer from an array-of-arrays
  const renderSimpleTable = (rows: (string | number)[][], key: string, options?: { boldLastRow?: boolean; boldRows?: Set<number> }) => {
    if (!rows || rows.length === 0) return null;
    const colCount = rows.reduce((m, r) => Math.max(m, r.length), 0);
    const base = colCount > 0 ? 100 / (colCount + 1) : 100;
    const firstPct = (2 * base).toFixed(6);
    const otherPct = base.toFixed(6);
    const keepTogether = rows.length <= MAX_ROWS_TO_KEEP_TOGETHER;
    const containerProps: any = keepTogether ? { wrap: false } : {};

    const renderSimpleRow = (row: (string | number)[], ri: number, keyPrefix: string) => {
      const isLastRow = ri === rows.length - 1;
      const isBoldRow = ri === 0 || options?.boldRows?.has(ri) || (isLastRow && options?.boldLastRow);
      return (
        <View key={`${keyPrefix}-${ri}`} style={tableStyles.tableRow} wrap={false}>
          {Array.from({ length: colCount }).map((_, ci) => {
            const isFirst = ci === 0;
            const raw = row[ci] ?? '';
            const finalCellStyle = [
              tableStyles.tableCell,
              isBoldRow ? tableStyles.tableHeaderCell : undefined,
              { width: `${isFirst ? firstPct : otherPct}%` },
            ] as any;
            const finalTextStyle = [!isFirst ? tableStyles.right : undefined] as any;
            return (
              <View key={`sc-${ci}`} style={finalCellStyle}>
                <Text style={finalTextStyle}>{String(raw)}</Text>
              </View>
            );
          })}
        </View>
      );
    };

    if (keepTogether) {
      return (
        <View key={key} style={tableStyles.container} {...containerProps}>
          <View style={tableStyles.table}>
            {rows.map((row, ri) => renderSimpleRow(row, ri, "sr"))}
          </View>
        </View>
      );
    }
    return (
      <View key={key} style={tableStyles.container}>
        <SplitAnchor />
        {/* Table wrapper provides the 100%-width container that row percent widths resolve against. */}
        <View style={tableStyles.table}>
          <SplitAnchor />
          <View wrap={false}>
            {renderSimpleRow(rows[0], 0, "sr-hdr")}
            {rows[1] ? renderSimpleRow(rows[1], 1, "sr") : null}
          </View>
          {rows.slice(2).map((row, i) => renderSimpleRow(row, i + 2, "sr"))}
        </View>
      </View>
    );
  };

  // ===== Income & Expenses helpers =====
  const getUserField = (key: string) => {
    try {
      const f = (modelDetails?.user_model_field_values || []).find((x: any) => x.field_key === key);
      return f?.value;
    } catch { return undefined; }
  };

  const renderUnitsTable = () => {
    const units: any[] = Array.isArray(modelDetails?.units) ? modelDetails.units : [];
    if (!units.length) return null;
    const mra: any[] = Array.isArray(modelDetails?.market_rent_assumptions) ? modelDetails.market_rent_assumptions : [];
    const header = ['Units','Layout','Square Feet','Current Rent','Rent Type','Pro Forma Rent'];
    let totalSf = 0, totalCurrent = 0, totalPf = 0;
    const dataRows = units.map((u: any, i: number) => {
      totalSf += Number(u.square_feet || 0);
      totalCurrent += Number(u.current_rent || 0);
      const pfAssump = mra.find((a: any) => a.layout === u.layout);
      const pfRent = pfAssump?.pf_rent;
      const displayPf = (u.vacate_flag === 0 || u.vacate_flag === '0')
        ? (u.current_rent != null ? `$${Number(u.current_rent).toLocaleString()}` : '')
        : (pfRent != null ? `$${Number(pfRent).toLocaleString()}` : '');
      totalPf += (u.vacate_flag === 0 || u.vacate_flag === '0') ? Number(u.current_rent || 0) : Number(pfRent || 0);
      return [
        String(i + 1),
        u.layout ?? '',
        u.square_feet != null ? Number(u.square_feet).toLocaleString() : '',
        u.current_rent != null ? `$${Number(u.current_rent).toLocaleString()}` : '',
        u.rent_type ?? '',
        displayPf,
      ];
    });

    const totalsRow = [
      'Totals',
      '',
      totalSf.toLocaleString(),
      `$${totalCurrent.toLocaleString()}`,
      '',
      `$${totalPf.toLocaleString()}`,
    ];
    const colCount = header.length;
    const base = 100 / (colCount + 1); // first column 2x
    const firstPct = (2 * base).toFixed(6);
    const otherPct = base.toFixed(6);

    const renderHeaderRow = (keyPrefix: string) => (
      <View style={[tableStyles.tableRow]}>
        {header.map((col, ci) => {
          const isFirst = ci === 0;
          return (
            <View key={`${keyPrefix}-${ci}`} style={[tableStyles.tableCell, tableStyles.tableHeaderCell, { width: `${isFirst ? firstPct : otherPct}%` }]}>
              <Text style={!isFirst ? tableStyles.right : undefined}>{col}</Text>
            </View>
          );
        })}
      </View>
    );
    const renderDataRow = (row: any[], ri: number) => (
      <View key={`u-r-${ri}`} style={tableStyles.tableRow} wrap={false}>
        {Array.from({ length: colCount }).map((_, ci) => {
          const isFirst = ci === 0;
          const raw = row[ci] ?? '';
          return (
            <View key={`u-c-${ri}-${ci}`} style={[tableStyles.tableCell, { width: `${isFirst ? firstPct : otherPct}%` }]}>
              <Text style={!isFirst ? tableStyles.right : undefined}>{String(raw)}</Text>
            </View>
          );
        })}
      </View>
    );

    return (
      <View style={styles.section}>
        <SplitAnchor />
        {/* Table wrapper gives rows a 100%-width container so cell percent widths resolve correctly. */}
        <View style={tableStyles.table}>
          <SplitAnchor />
          {/* Keep section title + column header + first data row together so titles never orphan. */}
          <View wrap={false}>
            <Text style={styles.sectionTitle}>Income and Expenses</Text>
            <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 6, marginBottom: 2 }}>Rent Roll</Text>
            {renderHeaderRow("u-hdr")}
            {dataRows.length > 0 ? renderDataRow(dataRows[0], 0) : null}
          </View>
          {dataRows.slice(1).map((row, i) => renderDataRow(row, i + 1))}
          {/* Totals row - bold all cells */}
          <View style={[tableStyles.tableRow, { backgroundColor: '#F3F4F6' }]} wrap={false}>
            {totalsRow.map((val, ci) => {
              const isFirst = ci === 0;
              return (
                <View key={`u-tot-${ci}`} style={[tableStyles.tableCell, { width: `${isFirst ? firstPct : otherPct}%` }]}>
                  <Text style={[{ fontWeight: 700 }, ...(!isFirst ? [tableStyles.right] : [])]}>{String(val)}</Text>
                </View>
              );
            })}
          </View>
        </View>
      </View>
    );
  };

  const renderDevelopmentUnitsTable = (sectionTitleText?: string) => {
    const rowsIn: any[] = Array.isArray(modelDetails?.development_units) ? modelDetails.development_units : [];
    if (!rowsIn.length) return null;
    const header = ['Unit Type','Avg. SF','Units','Total SF','Rent PSF/Mo','Avg. Rent','Monthly Rent','Annual Rent'];
    let totals = { units: 0, totalSf: 0, monthlyRent: 0, annualRent: 0 };
    const dataRows = rowsIn.map((r: any) => {
      const avgSf = Number(r?.avg_sf || 0);
      const units = Number(r?.units || 0);
      const avgRentMonthly = Number(r?.avg_rent || 0);
      const totalSf = Math.max(0, Math.round(avgSf * units));
      const monthlyRent = Math.max(0, avgRentMonthly * units);
      const annualRent = monthlyRent * 12;
      const rentPsf = avgSf > 0 ? avgRentMonthly / avgSf : 0;
      totals.units += units;
      totals.totalSf += totalSf;
      totals.monthlyRent += monthlyRent;
      totals.annualRent += annualRent;
      return [
        r?.unit_type ?? '',
        Number(avgSf).toLocaleString(),
        Number(units).toLocaleString(),
        Number(totalSf).toLocaleString(),
        formatPSF(rentPsf),
        `$${Number(avgRentMonthly).toLocaleString()}`,
        `$${Number(monthlyRent).toLocaleString()}`,
        `$${Number(annualRent).toLocaleString()}`,
      ];
    });
    const weightedRentPsf = totals.totalSf > 0 ? totals.monthlyRent / totals.totalSf : 0;
    const avgRent = totals.units > 0 ? Math.round(totals.monthlyRent / totals.units) : 0;
    const totalsRow = [
      'Totals',
      '',
      totals.units.toLocaleString(),
      totals.totalSf.toLocaleString(),
      formatPSF(weightedRentPsf),
      `$${avgRent.toLocaleString()}`,
      `$${totals.monthlyRent.toLocaleString()}`,
      `$${totals.annualRent.toLocaleString()}`,
    ];
    const allRows = [header, ...dataRows, totalsRow];
    const colCount = header.length;
    const base = 100 / (colCount + 1);
    const firstPct = (2 * base).toFixed(6);
    const otherPct = base.toFixed(6);
    const renderDevRow = (row: any[], ri: number, keyPrefix: string) => {
      const isHeader = ri === 0;
      const isTotals = ri === allRows.length - 1;
      return (
        <View key={`${keyPrefix}-${ri}`} style={tableStyles.tableRow} wrap={false}>
          {Array.from({ length: colCount }).map((_, ci) => {
            const isFirst = ci === 0;
            const raw = row[ci] ?? '';
            const finalCellStyle = [
              tableStyles.tableCell,
              isHeader ? tableStyles.tableHeaderCell : undefined,
              isTotals ? { backgroundColor: '#F3F4F6' } : undefined,
              { width: `${isFirst ? firstPct : otherPct}%` },
            ] as any;
            const finalTextStyle = [
              !isFirst ? tableStyles.right : undefined,
              isTotals && isFirst ? { fontWeight: 700 } : undefined,
            ] as any;
            return (
              <View key={`du-c-${ri}-${ci}`} style={finalCellStyle}>
                <Text style={finalTextStyle}>{String(raw)}</Text>
              </View>
            );
          })}
        </View>
      );
    };
    const keepTogether = allRows.length <= MAX_ROWS_TO_KEEP_TOGETHER;
    if (keepTogether) {
      return (
        <View style={styles.section} wrap={false}>
          {sectionTitleText ? <Text style={styles.sectionTitle}>{sectionTitleText}</Text> : null}
          <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 6 }}>Development Units</Text>
          <View style={tableStyles.table}>
            {allRows.map((row, ri) => renderDevRow(row, ri, "du-r"))}
          </View>
        </View>
      );
    }
    return (
      <View style={styles.section}>
        <SplitAnchor />
        <View style={tableStyles.table}>
          <SplitAnchor />
          {/* Keep intro (optional section title + sub-title + header + first data row) together to avoid orphans */}
          <View wrap={false}>
            {sectionTitleText ? <Text style={styles.sectionTitle}>{sectionTitleText}</Text> : null}
            <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 6 }}>Development Units</Text>
            {renderDevRow(allRows[0], 0, "du-hdr")}
            {allRows[1] ? renderDevRow(allRows[1], 1, "du-r") : null}
          </View>
          {allRows.slice(2).map((row, i) => renderDevRow(row, i + 2, "du-r"))}
        </View>
      </View>
    );
  };

  const renderAmenityIncomeTable = () => {
    const list: any[] = Array.isArray(modelDetails?.amenity_income) ? modelDetails.amenity_income : [];
    if (!list.length) return null;
    const header = ['Name','Start Month','Utilization','Unit Count','Monthly Fee','Usage','Monthly','Annual'];
    let totalMonthly = 0, totalAnnual = 0;
    const rows = [header, ...list.map((row: any) => {
      const utilization = Number(row.utilization || 0);
      const unitCount = Number(row.unit_count || 0);
      const usage = Math.round((utilization / 100) * unitCount);
      const monthly = usage * Number(row.monthly_fee || 0);
      const annual = monthly * 12;
      totalMonthly += monthly; totalAnnual += annual;
      return [
        row.name ?? '',
        row.start_month != null ? `month ${row.start_month}` : '',
        `${utilization}%`,
        `${unitCount} units`,
        row.monthly_fee != null ? `$${Number(row.monthly_fee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '',
        `${usage} Units`,
        `$${Number(monthly).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        `$${Number(annual).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      ];
    })];
    const totalsRow = [
      'Totals', '', '', '', '', '',
      `$${Math.round(totalMonthly).toLocaleString()}`,
      `$${Math.round(totalAnnual).toLocaleString()}`
    ];
    const allRows = [...rows, totalsRow];
    const colCount = header.length;
    const base = 100 / (colCount + 1);
    const firstPct = (2 * base).toFixed(6);
    const otherPct = base.toFixed(6);
    const renderAmRow = (row: any[], ri: number, keyPrefix: string) => {
      const isHeader = ri === 0;
      const isTotals = ri === allRows.length - 1;
      return (
        <View key={`${keyPrefix}-${ri}`} style={tableStyles.tableRow} wrap={false}>
          {Array.from({ length: colCount }).map((_, ci) => {
            const isFirst = ci === 0;
            const raw = row[ci] ?? '';
            const finalCellStyle = [
              tableStyles.tableCell,
              isHeader ? tableStyles.tableHeaderCell : undefined,
              isTotals ? { backgroundColor: '#F3F4F6' } : undefined,
              { width: `${isFirst ? firstPct : otherPct}%` },
            ] as any;
            const finalTextStyle = [
              !isFirst ? tableStyles.right : undefined,
              isTotals ? { fontWeight: 700 } : undefined,
            ] as any;
            return (
              <View key={`am-c-${ri}-${ci}`} style={finalCellStyle}>
                <Text style={finalTextStyle}>{String(raw)}</Text>
              </View>
            );
          })}
        </View>
      );
    };
    const keepAmTogether = allRows.length <= MAX_ROWS_TO_KEEP_TOGETHER;
    if (keepAmTogether) {
      return (
        <View style={styles.section} wrap={false}>
          <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 12 }}>Amenity Income</Text>
          <View style={tableStyles.table}>
            {allRows.map((row, ri) => renderAmRow(row, ri, "am-r"))}
          </View>
        </View>
      );
    }
    return (
      <View style={styles.section}>
        <SplitAnchor />
        <View style={tableStyles.table}>
          <SplitAnchor />
          <View wrap={false}>
            <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 12 }}>Amenity Income</Text>
            {renderAmRow(allRows[0], 0, "am-hdr")}
            {allRows[1] ? renderAmRow(allRows[1], 1, "am-r") : null}
          </View>
          {allRows.slice(2).map((row, i) => renderAmRow(row, i + 2, "am-r"))}
        </View>
      </View>
    );
  };

  const renderOperatingExpensesTable = () => {
    const list: any[] = Array.isArray(modelDetails?.operating_expenses) ? modelDetails.operating_expenses : [];
    if (!list.length) return null;
    const header = ['Name','Cost per','Expense','Statistic','Monthly','Annual'];
    const units: any[] = Array.isArray(modelDetails?.units) ? modelDetails.units : [];
    const retailIncome: any[] = Array.isArray(modelDetails?.retail_income) ? modelDetails.retail_income : [];
    const retailExpenses: any[] = Array.isArray(modelDetails?.expenses) ? modelDetails.expenses.filter((e:any)=>e.type==='Retail') : [];
    const amenityIncome: any[] = Array.isArray(modelDetails?.amenity_income) ? modelDetails.amenity_income : [];
    const developmentUnits: any[] = Array.isArray(modelDetails?.development_units) ? modelDetails.development_units : [];
    const isDevelopmentModel = modelDetails?.model_type?.development_model === true || developmentUnits.length > 0;
    const perUnitCount = isDevelopmentModel && developmentUnits.length > 0
      ? developmentUnits.reduce((s: number, du: any) => s + Number(du?.units || 0), 0)
      : units.length;
    const commonAreaSf = getCommonAreaSquareFeet(modelDetails, units, developmentUnits, retailIncome, isDevelopmentModel);
    const totalSfForExpense = getTotalSquareFeetForExpense(modelDetails, isDevelopmentModel, developmentUnits);
    const getRetailIncomeTotal = (arr: any[]) => arr.reduce((sum: number, u: any) => sum + (Number(u.square_feet || 0) * Number(u.rent_per_square_foot_per_year || 0)), 0);
    const totalRetailIncome = getRetailIncomeTotal(retailIncome);
    const egi = (() => {
      if (isDevelopmentModel && developmentUnits.length > 0) {
        const devAnnualRental = developmentUnits.reduce((s: number, du: any) => {
          const n = Number(du?.units || 0);
          const rent = Number(du?.avg_rent || 0);
          return s + (Number.isFinite(n) && Number.isFinite(rent) ? n * rent * 12 : 0);
        }, 0);
        const amenMonthly = amenityIncome.reduce((sum, a:any)=>{
          const util = Number(a.utilization || 0); const cnt = Number(a.unit_count || 0); const usage = Math.round((util/100)*cnt); return sum + usage*Number(a.monthly_fee||0);
        },0);
        return devAnnualRental + amenMonthly*12 + totalRetailIncome;
      }
      const current = units.reduce((sum, u:any)=> sum + Number(u.current_rent || 0), 0) * 12;
      const amenMonthly = amenityIncome.reduce((sum, a:any)=>{
        const util = Number(a.utilization || 0); const cnt = Number(a.unit_count || 0); const usage = Math.round((util/100)*cnt); return sum + usage*Number(a.monthly_fee||0);
      },0);
      return current + amenMonthly*12 + totalRetailIncome;
    })();
    let totalMonthly = 0, totalAnnual = 0;
    const rows = [header, ...list.map((row:any)=>{
      const byUnit = String(row.cost_per || '').toLowerCase();
      let statistic: string | number | null = null;
      if (byUnit === 'per unit') statistic = `${perUnitCount} units`;
      else if (byUnit === 'per ca square foot') statistic = `${commonAreaSf.toLocaleString()} sf`;
      else if (byUnit === 'per total square feet') statistic = `${totalSfForExpense.toLocaleString()} sf`;
      const expenseVal = Number(row.factor || 0);
      let monthly = 0, annual = 0;
      if (byUnit === 'per unit') { annual = expenseVal * perUnitCount; monthly = annual/12; }
      else if (byUnit === 'total') { annual = expenseVal; monthly = annual/12; }
      else if (byUnit === 'per ca square foot') { annual = Math.round(expenseVal * commonAreaSf); monthly = annual/12; }
      else if (byUnit === 'per total square feet') { annual = Math.round(expenseVal * totalSfForExpense); monthly = annual/12; }
      else if (byUnit === 'percent of egi') { annual = (expenseVal * egi)/100; monthly = annual/12; }
      totalMonthly += monthly; totalAnnual += annual;
      return [
        row.name ?? '',
        row.cost_per ?? '',
        byUnit === 'percent of egi' ? `${expenseVal}%` : (['per unit','total','per ca square foot','per total square feet'].includes(byUnit) ? `$${Number(expenseVal).toLocaleString()}` : String(expenseVal)),
        statistic ?? '',
        `$${Number(monthly).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}`,
        `$${Number(annual).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}`,
      ];
    })];

    const totalsRow = [
      'Totals', '', '', '',
      `$${Number(totalMonthly).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}`,
      `$${Number(totalAnnual).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}`,
    ];
    const allRows = [...rows, totalsRow];
    const colCount = header.length;
    const base = 100 / (colCount + 1);
    const firstPct = (2 * base).toFixed(6);
    const otherPct = base.toFixed(6);

    const renderOpRow = (row: any[], ri: number, keyPrefix: string) => {
      const isHeader = ri === 0;
      const isTotals = ri === allRows.length - 1;
      return (
        <View key={`${keyPrefix}-${ri}`} style={tableStyles.tableRow} wrap={false}>
          {Array.from({ length: colCount }).map((_, ci) => {
            const isFirst = ci === 0;
            const raw = row[ci] ?? '';
            const finalCellStyle = [
              tableStyles.tableCell,
              isHeader ? tableStyles.tableHeaderCell : undefined,
              isTotals ? { backgroundColor: '#F3F4F6' } : undefined,
              { width: `${isFirst ? firstPct : otherPct}%` },
            ] as any;
            const finalTextStyle = [
              !isFirst ? tableStyles.right : undefined,
              isTotals ? { fontWeight: 700 } : undefined,
            ] as any;
            return (
              <View key={`op-c-${ri}-${ci}`} style={finalCellStyle}>
                <Text style={finalTextStyle}>{String(raw)}</Text>
              </View>
            );
          })}
        </View>
      );
    };
    const keepOpTogether = allRows.length <= MAX_ROWS_TO_KEEP_TOGETHER;
    if (keepOpTogether) {
      return (
        <View style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>OPERATING EXPENSES</Text>
          <View style={tableStyles.table}>
            {allRows.map((row, ri) => renderOpRow(row, ri, "op-r"))}
          </View>
        </View>
      );
    }
    return (
      <View style={styles.section}>
        <SplitAnchor />
        <View style={tableStyles.table}>
          <SplitAnchor />
          <View wrap={false}>
            <Text style={styles.sectionTitle}>OPERATING EXPENSES</Text>
            {renderOpRow(allRows[0], 0, "op-hdr")}
            {allRows[1] ? renderOpRow(allRows[1], 1, "op-r") : null}
          </View>
          {allRows.slice(2).map((row, i) => renderOpRow(row, i + 2, "op-r"))}
        </View>
      </View>
    );
  };

  // ===== Retail Summary equivalents (read-only) =====
  const renderRetailBaseIncomeTable = () => {
    const income: any[] = Array.isArray(modelDetails?.retail_income) ? modelDetails.retail_income : [];
    if (!income.length) return null;
    const header = ['Suite','Tenant','Square Ft','Rent PSF/Year','Rent Type','Annual Rent'];
    let totalAnnual = 0;
    const dataRows = income.map((r: any) => {
      const sf = Number(r.square_feet || 0);
      const psf = Number(r.rent_per_square_foot_per_year || 0);
      const annual = psf * sf;
      totalAnnual += annual;
      return [
        r.suite ?? '',
        r.tenant_name ?? '',
        sf ? Number(sf).toLocaleString() : '',
        psf ? `$${psf}` : '',
        r.rent_type ?? 'Gross',
        `$${Number(annual).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`,
      ];
    });
    const totalsRow = [
      'Totals', '', '', '', '',
      `$${Number(totalAnnual).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`,
    ];
    const allRows = [header, ...dataRows, totalsRow];
    const colCount = header.length;
    const base = 100 / (colCount + 1); // first column 2x
    const firstPct = (2 * base).toFixed(6);
    const otherPct = base.toFixed(6);
    const renderRetailRow = (row: any[], ri: number, keyPrefix: string) => {
      const isHeader = ri === 0;
      const isTotals = ri === allRows.length - 1;
      return (
        <View key={`${keyPrefix}-${ri}`} style={tableStyles.tableRow} wrap={false}>
          {Array.from({ length: colCount }).map((_, ci) => {
            const isFirst = ci === 0;
            const raw = row[ci] ?? '';
            const finalCellStyle = [
              tableStyles.tableCell,
              isHeader ? tableStyles.tableHeaderCell : undefined,
              isTotals ? { backgroundColor: '#F3F4F6' } : undefined,
              { width: `${isFirst ? firstPct : otherPct}%` },
            ] as any;
            const finalTextStyle = [
              !isFirst ? tableStyles.right : undefined,
              isTotals && isFirst ? { fontWeight: 700 } : undefined,
            ] as any;
            return (
              <View key={`rb-c-${ri}-${ci}`} style={finalCellStyle}>
                <Text style={finalTextStyle}>{String(raw)}</Text>
              </View>
            );
          })}
        </View>
      );
    };
    const keepRetailTogether = allRows.length <= MAX_ROWS_TO_KEEP_TOGETHER;
    if (keepRetailTogether) {
      return (
        <View style={tableStyles.container} wrap={false}>
          <View style={tableStyles.table}>
            {allRows.map((row, ri) => renderRetailRow(row, ri, "rb-r"))}
          </View>
        </View>
      );
    }
    return (
      <View style={tableStyles.container}>
        <SplitAnchor />
        <View style={tableStyles.table}>
          <SplitAnchor />
          <View wrap={false}>
            {renderRetailRow(allRows[0], 0, "rb-hdr")}
            {allRows[1] ? renderRetailRow(allRows[1], 1, "rb-r") : null}
          </View>
          {allRows.slice(2).map((row, i) => renderRetailRow(row, i + 2, "rb-r"))}
        </View>
      </View>
    );
  };

  const renderRetailGrossPotential = () => {
    const getFV = (k: string) => {
      try {
        const f = (modelDetails?.user_model_field_values || []).find((x: any) => x.field_key === k || String(x.field_key || '').trim() === k.trim());
        return f?.value;
      } catch { return undefined; }
    };
    const vacancy = Number(getFV('Vacancy ') ?? getFV('Vacancy') ?? 5);
    const vacancyRate = vacancy / 100;
    const retailIncome: any[] = Array.isArray(modelDetails?.retail_income) ? modelDetails.retail_income : [];
    const totalSF = retailIncome.reduce((s, r) => s + Number(r.square_feet || 0), 0);
    const baseAnnual = retailIncome.reduce((s, r) => s + Number(r.square_feet || 0) * Number(r.rent_per_square_foot_per_year || 0), 0);
    const expenses: any[] = Array.isArray(modelDetails?.expenses) ? modelDetails.expenses.filter((e:any)=>String(e?.type||'').toLowerCase()==='retail') : [];
    const computeAnnual = (e:any) => {
      const factor = String(e?.factor || '').toLowerCase();
      const cost = Number(e?.cost_per || 0);
      if (factor === 'annual') return cost;
      if (factor === 'per sf / yr.' || factor === 'per sf') return cost * Number(totalSF || 0);
      if (factor === 'percent of base rent') return (cost/100) * Number(baseAnnual || 0);
      return cost;
    };
    const annualBoth = expenses.filter((e:any)=>String(e?.rent_type_included || '').toLowerCase()==='both').reduce((s:number,e:any)=>s+computeAnnual(e),0);
    const annualGross = expenses.filter((e:any)=>String(e?.rent_type_included || '').toLowerCase()==='gross').reduce((s:number,e:any)=>s+computeAnnual(e),0);
    const annualNNN = expenses.filter((e:any)=>String(e?.rent_type_included || '').toLowerCase()==='nnn').reduce((s:number,e:any)=>s+computeAnnual(e),0);
    const grossSF = retailIncome.filter((r:any)=>String(r?.rent_type || 'Gross').toLowerCase()==='gross').reduce((s:number,r:any)=>s+Number(r.square_feet||0),0);
    const nnnSF = retailIncome.filter((r:any)=>String(r?.rent_type || 'Gross').toLowerCase()==='nnn').reduce((s:number,r:any)=>s+Number(r.square_feet||0),0);
    const shareGross = totalSF ? grossSF/totalSF : 0;
    const shareNNN = totalSF ? nnnSF/totalSF : 0;
    const totalAnnualRecoveryIncome = annualBoth + shareGross*annualGross + shareNNN*annualNNN;
    const beforeAnnual = baseAnnual + totalAnnualRecoveryIncome;
    const beforePerSf = totalSF ? beforeAnnual/totalSF : 0;
    const vacAnnual = beforeAnnual * vacancyRate;
    const vacPerSf = totalSF ? vacAnnual/totalSF : 0;
    const afterPerSf = beforePerSf - vacPerSf;
    const afterAnnual = beforeAnnual - vacAnnual;
    const money0 = (n:number) => `$${Math.round(n).toLocaleString()}`;
    const money2 = (n:number) => `$${n.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`;
    const vacLabel = `Less: Vacancy and Bad Debt (${vacancy.toFixed(1)}%)`;
    const rows: (string|number)[][] = [
      ['', 'Rent / SF / Yr.', 'Monthly', 'Annual'],
      ['Gross Potential Income', money2(beforePerSf), money0(beforeAnnual / 12), money0(beforeAnnual)],
      [vacLabel, money2(vacPerSf), money0(vacAnnual / 12), money0(vacAnnual)],
      ['Effective Gross Income', money2(afterPerSf), money0(afterAnnual / 12), money0(afterAnnual)],
    ];
    return renderSimpleTable(rows, 'retail-gpi', { boldRows: new Set([1, 3]) });
  };

  const renderRetailExpenses = () => {
    const list: any[] = Array.isArray(modelDetails?.expenses) ? modelDetails.expenses.filter((e:any)=>String(e?.type||'').toLowerCase()==='retail') : [];
    if (!list.length) return null;
    const header = ['Name','Factor','Expense','Statistic','Rent Type Included','Annual'];
    const retailIncome: any[] = Array.isArray(modelDetails?.retail_income) ? modelDetails.retail_income : [];
    const totalSF = retailIncome.reduce((s: number, r: any) => s + Number(r.square_feet || 0), 0);
    const baseAnnual = retailIncome.reduce(
      (s: number, r: any) => s + Number(r.square_feet || 0) * Number(r.rent_per_square_foot_per_year || 0), 0
    );

    let totalAnnual = 0;
    const rows = [header, ...list.map((row: any) => {
      const factor = String(row.factor || 'Annual');
      const factorLower = factor.toLowerCase();
      const cost = Number(row.cost_per || 0);

      let statistic = '\u2014';
      let annual = cost;
      if (factorLower === 'per sf / yr.' || factorLower === 'per sf' || factorLower === 'per ca square foot' || factorLower === 'per total square feet') {
        statistic = `${Number(totalSF).toLocaleString()} sf`;
        annual = cost * totalSF;
      } else if (factorLower === 'percent of base rent') {
        statistic = `$${Number(baseAnnual).toLocaleString()}`;
        annual = (cost / 100) * baseAnnual;
      }
      totalAnnual += annual;

      let expenseDisplay: string;
      if (factorLower === 'percent of base rent') {
        expenseDisplay = `${cost}%`;
      } else {
        expenseDisplay = `$${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }

      return [
        row.name ?? '',
        factor,
        expenseDisplay,
        statistic,
        row.rent_type_included ?? 'Both',
        `$${Math.round(annual).toLocaleString()}`,
      ];
    })];
    const totalsRow = [
      '', '', '', '', 'Total Expenses:',
      `$${Math.round(totalAnnual).toLocaleString()}`,
    ];
    const allRows = [...rows, totalsRow];
    return renderSimpleTable(allRows, 'retail-expenses');
  };

  const renderRetailLeasingCostReserves = () => {
    const getFV = (k: string, def: number) => {
      try {
        const f = (modelDetails?.user_model_field_values || []).find((x: any) => x.field_key === k);
        const v = f ? Number(f.value) : def;
        return Number.isFinite(v) ? v : def;
      } catch { return def; }
    };
    const retailIncome: any[] = Array.isArray(modelDetails?.retail_income) ? modelDetails.retail_income : [];
    const totalSF = retailIncome.reduce((s, r) => s + Number(r.square_feet || 0), 0);
    const renewalProb = Number(getFV('Renewal Property: Renewal Lease', 1.5) || 0);
    const newProb = Math.max(0, 100 - renewalProb);
    const rentNew = Number(getFV('Retail Rent: New Lease', 0) || 0);
    const rentRen = Number(getFV('Retail Rent: Renewal Lease', 0) || 0);
    const tiNewPSF = Number(getFV("TI's: New Lease", 0) || 0);
    const tiRenPSF = Number(getFV("TI's: Renewal Lease", 0) || 0);
    const commNewPct = Number(getFV('Leasing Commissions: New Lease', 0) || 0) / 100;
    const commRenPct = Number(getFV('Leasing Commissions: Renewal Lease', 0) || 0) / 100;
    const termNewYrs = Number(getFV('Lease Term: New Lease', 1) || 1);
    const termRenYrs = Number(getFV('Lease Term: Renewal Lease', 1) || 1);
    const tiNewAmt = tiNewPSF * totalSF;
    const tiRenAmt = tiRenPSF * totalSF;
    const displayTINewAmt = tiNewAmt * (newProb / 100);
    const displayTIRenAmt = tiRenAmt * (renewalProb / 100);
    const commNewAmt = totalSF * (newProb / 100) * rentNew * commNewPct * termNewYrs;
    const commRenAmt = totalSF * (renewalProb / 100) * rentRen * commRenPct * termRenYrs;
    const totalLCNew = commNewAmt + displayTINewAmt;
    const totalLCRen = commRenAmt + displayTIRenAmt;
    const amortNew = termNewYrs ? totalLCNew / termNewYrs : 0;
    const amortRen = termRenYrs ? totalLCRen / termRenYrs : 0;
    const weightedAnnual = amortNew + amortRen;
    const weightedPerSF = totalSF ? weightedAnnual / totalSF : 0;
    const money0 = (n:number) => `$${Number(n).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:0})}`;
    const money2 = (n:number) => `$${Number(n).toFixed(2)}`;
    const rows: (string|number)[][] = [
      ['', 'New Lease', 'Renewal Lease'],
      ['Tenant Improvements', money0(displayTINewAmt), money0(displayTIRenAmt)],
      ['Leasing Commissions', money0(commNewAmt), money0(commRenAmt)],
      ['Total Leasing Costs', money0(totalLCNew), money0(totalLCRen)],
      ['Annualized Leasing Costs', money0(amortNew), money0(amortRen)],
      ['Annual TILC Reserves', `${money2(weightedPerSF)} / SF`, money0(weightedAnnual)],
    ];
    return renderSimpleTable(rows, 'retail-lcr', { boldLastRow: true });
  };

  const resolvedSpaceType = (() => {
    if (spaceType && typeof spaceType === 'string') return spaceType;
    try {
      const f = (modelDetails?.user_model_field_values || []).find((x: any) => {
        const k = String(x.field_key || '');
        return k === 'space_type' || k.trim() === 'space_type';
      });
      return f?.value ?? 'Retail';
    } catch {
      return 'Retail';
    }
  })();
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.title}>{modelDetails?.name ?? "Model Summary"}</Text>
            <Text style={styles.subTitle}>{addressLine}</Text>
          </View>
          <View style={[styles.companyBlock, styles.headerRight]}>
            {options["Include Company Logo"] && (logoSrc || (companyInfo && companyInfo.company_logo_url)) ? (
              <View style={styles.logoWrap}>
                <Image style={styles.logo} src={logoSrc || (companyInfo?.company_logo_url as any)} />
              </View>
            ) : null}
            {options["Include Company Info"] && (companyInfo?.company_name || companyInfo?.company_phone_number || companyInfo?.company_email) ? (
              <View>
                {companyInfo?.company_name ? <Text style={styles.companyLine}>{companyInfo.company_name}</Text> : null}
                {companyInfo?.company_phone_number ? <Text style={styles.companyLine}>{companyInfo.company_phone_number}</Text> : null}
                {companyInfo?.company_email ? <Text style={styles.companyLine}>{companyInfo.company_email}</Text> : null}
              </View>
            ) : null}
          </View>
        </View>

        

        {/* Render sections in the provided order. No forced page breaks — content flows naturally and spills onto the next page only when it runs out of room. */}
        {Array.isArray(order) && order.length > 0
          ? (() => {
              // Track which keys to skip because they were merged with a preceding section
              const skipKeys = new Set<number>();
              return order.map((key, idx) => {
              if (skipKeys.has(idx)) return null;
              // Handle named tables from otherTables
              const tbl = (otherTables as any[]).find((t: any) => t?.table_name === key);

              if (key === "Summary Info" && options["Summary Info"]) {
                return (
                  <View key={`sec-${idx}`} style={styles.section}>
                    <SplitAnchor />
            {summaryTables && summaryTables.length > 0 ? (
              <View style={styles.section}>
                <SplitAnchor />
                        {summaryTables.map((tbl: any, i: number) => (
                          <View key={`sum-${i}`} wrap={false} style={{ marginBottom: i < summaryTables.length - 1 ? 12 : 0 }}>
                            {/* Pin the Summary Info section title to the first sub-table so it never orphans. */}
                            {i === 0 ? (
                              <Text style={styles.sectionTitle}>Summary Info</Text>
                            ) : null}
                            {tbl?.table_name ? (
                              <Text style={{ fontSize: 10, fontWeight: 700, marginBottom: 4 }}>{tbl.table_name}</Text>
                            ) : null}
                            {renderPdfTable(tbl, `sum-${i}`, undefined, { disableWrapFalse: true })}
                          </View>
                        ))}
              </View>
            ) : (
              <Text style={{ fontSize: 10, color: "#6B7280", marginTop: 4 }}>No summary tables for this model.</Text>
            )}
                  </View>
                );
              }

              if (key === "KEY PERFORMANCE METRICS" && options["KEY PERFORMANCE METRICS"]) {
                const primaryKeys = new Set(["Levered IRR", "Levered MOIC"]);
                const extraVariableEntries = Object.entries(variables || {})
                  .filter(([k, v]) => {
                    if (primaryKeys.has(k)) return false;
                    if (v === undefined || v === null || v === "") return false;
                    const s = String(v);
                    if (s.length > 220) return false;
                    return true;
                  })
                  .sort(([a], [b]) => a.localeCompare(b))
                  .slice(0, 24);
                return (
                  <View key={`sec-${idx}`} style={styles.section} wrap={false}>
                    <Text style={styles.sectionTitle}>KEY PERFORMANCE METRICS</Text>
                    <View style={styles.grid}>
                      <View style={styles.gridItemThird}>
                        <Text style={styles.label}>Levered IRR</Text>
                        <Text style={styles.value}>
                          {formatKpiValue(modelDetails?.levered_irr ?? variables?.["Levered IRR"], "irr")}
                        </Text>
                      </View>
                      <View style={styles.gridItemThird}>
                        <Text style={styles.label}>Levered MOIC</Text>
                        <Text style={styles.value}>
                          {formatKpiValue(modelDetails?.levered_moic ?? variables?.["Levered MOIC"], "moic")}
                        </Text>
                      </View>
                      <View style={styles.gridItemThird}>
                        <Text style={styles.label}>Hold Period</Text>
                        <Text style={styles.value}>
                          {holdMonths !== null ? `${String(holdMonths)} Months` : "-"}
                        </Text>
                      </View>
                    </View>
                   
                  </View>
                );
              }

              if (key === "Sensitivity Tables" && options["Sensitivity Tables"]) {
                return (
                  <View key={`sec-${idx}`} style={styles.section}>
                    <SplitAnchor />
            <Text style={styles.sectionTitle}>Sensitivity Tables</Text>
            {irrTable ? (
              <View wrap={false}>
                <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 6 }}>Levered IRR Sensitivity Analysis</Text>
                <Text style={{ fontSize: 8, color: '#6B7280' }}>Exit Cap Rate (%) vs Acquisition Price ($)</Text>
                {renderSensitivity(irrTable, 'irr', (n) => `${n.toFixed(1)}%`, 'Exit Cap Rate (%)')}
              </View>
            ) : null}
            {moicTable ? (
              <View wrap={false}>
                <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 12 }}>Levered MOIC Sensitivity Analysis</Text>
                <Text style={{ fontSize: 8, color: '#6B7280' }}>Exit Cap Rate (%) vs Acquisition Price ($)</Text>
                {renderSensitivity(moicTable, 'moic', (n) => `${n.toFixed(2)}x`, 'Exit Cap Rate (%)')}
              </View>
            ) : null}
          </View>
                );
              }

              if (key === "Income and Expenses" && options["Income and Expenses"]) {
                const isRetail = retailMode ||
                  (modelDetails?.model_type?.show_rental_units === false && modelDetails?.model_type?.show_retail === true);
                return (
                  <View key={`sec-${idx}`} style={styles.section}>
                    <SplitAnchor />
            {isRetail ? (
              <>
                {/* Pin the "Income and Expenses" title to the first sub-block so it never orphans. */}
                <View wrap={false}>
                  <Text style={styles.sectionTitle}>Income and Expenses</Text>
                  <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 20 }}>Base Income</Text>
                  {renderRetailBaseIncomeTable()}
                </View>
                <View wrap={false}>
                  <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 20 }}>Gross Potential Income</Text>
                  {renderRetailGrossPotential()}
                </View>
                <View wrap={false}>
                  <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 20 }}>Recoverable Operating Expenses</Text>
                  {renderRetailExpenses()}
                </View>
                <View wrap={false}>
                  <Text style={{ fontSize: 10, fontWeight: 700, marginTop: 20 }}>TILC Reserves</Text>
                  {renderRetailLeasingCostReserves()}
                </View>
              </>
            ) : (
              <>
                {(Array.isArray(modelDetails?.development_units) && modelDetails.development_units.length > 0)
                  ? renderDevelopmentUnitsTable("Income and Expenses") /* title stays pinned to first dev-units row */
                  : renderUnitsTable() /* renderUnitsTable pins its own title to the first rent-roll row */}
                {renderAmenityIncomeTable()}
                {renderOperatingExpensesTable()}
              </>
            )}
          </View>
                );
              }

              if (key === "Property Images" && options["Property Images"] && Array.isArray(pictures) && pictures.length > 0) {
                // Filter to only pictures with valid data URLs (raw URLs fail in react-pdf due to CORS)
                const validPictures = pictures.filter(p => {
                  const url = String(p?.picture_url || "");
                  return url.startsWith("data:");
                });
                if (validPictures.length === 0) return null;
                return (
                  <View key={`sec-${idx}`} style={styles.section}>
                    <SplitAnchor />
                    <Text style={styles.sectionTitle}>Property Images</Text>
                    <View wrap={false} style={{ marginBottom: 8, width: FULL_IMG_WIDTH, maxWidth: FULL_IMG_WIDTH, overflow: "hidden" }}>
                      <Image
                        src={String(validPictures[0]?.picture_url)}
                        style={{ height: 280, maxWidth: FULL_IMG_WIDTH, objectFit: "contain", alignSelf: "flex-start" }}
                      />
                      {(() => {
                        const desc = String(validPictures[0]?.description || "").trim();
                        return desc
                          ? (<Text style={{ fontSize: 10, color: "#6B7280", marginTop: 4 }}>{desc}</Text>)
                          : null;
                      })()}
                    </View>
                    <View style={{ flexDirection: "row", flexWrap: "wrap", width: FULL_IMG_WIDTH, maxWidth: FULL_IMG_WIDTH }}>
                      {validPictures.slice(1).map((p, i) => (
                        <View key={`pic-${i}`} wrap={false} style={{ width: HALF_CELL_WIDTH, maxWidth: HALF_CELL_WIDTH, paddingRight: IMG_GUTTER, paddingBottom: IMG_GUTTER, overflow: "hidden" }}>
                          <Image
                            src={String(p.picture_url)}
                            style={{ height: 180, maxWidth: HALF_IMG_WIDTH, objectFit: "contain", alignSelf: "flex-start" }}
                          />
                          {(() => {
                            const desc = String(p.description || "").trim();
                            return desc
                              ? (<Text style={{ fontSize: 10, color: "#6B7280", marginTop: 4 }}>{desc}</Text>)
                              : null;
                          })()}
                        </View>
                      ))}
                    </View>
                  </View>
                );
              }

              if (key === "Include Notes" && options["Include Notes"]) {
                if (Array.isArray(notes) && notes.length > 0) {
                  return (
                    <View key={`sec-${idx}`} style={styles.section}>
                      <SplitAnchor />
                      <Text style={styles.sectionTitle}>Notes</Text>
                      <View style={{ marginTop: 6 }}>
                        <SplitAnchor />
                        {notes.map((n, i) => {
                          const isLast = i === notes.length - 1;
                          return (
                            <View
                              key={`note-${i}`}
                              wrap={false}
                              style={{
                                marginBottom: 6,
                                paddingBottom: 6,
                                borderBottomWidth: isLast ? 0 : 1,
                                borderBottomColor: "#E5E7EB",
                                borderBottomStyle: "solid",
                              }}
                            >
                              <Text style={{ fontSize: 10, color: "#374151", lineHeight: 1.4 as any }}>
                                {String(n)}
                              </Text>
                            </View>
                          );
                        })}
                      </View>
                    </View>
                  );
                }
                return null;
              }

              // Handle named table — group "Exit Summary" + "Exit Valuation and Returns" on one page
              if (tbl && options[key]) {
                // Check if next section should be merged (Exit Summary → Exit Valuation and Returns)
                const nextKey = order[idx + 1];
                const nextTbl = nextKey ? (otherTables as any[]).find((t: any) => t?.table_name === nextKey) : null;
                const shouldMergeNext =
                  key === "Exit Summary" &&
                  nextKey === "Exit Valuation and Returns" &&
                  nextTbl &&
                  options[nextKey];

                if (shouldMergeNext) {
                  skipKeys.add(idx + 1);
                  // Only force these two onto one page when both are small enough that the
                  // combined block can't itself exceed a page (same MAX_ROWS_TO_KEEP_TOGETHER
                  // safety margin used everywhere else in this file). Otherwise fall back to
                  // each table's own orphan-protected, paginate-safe rendering so a long Exit
                  // Valuation and Returns table doesn't get clipped at the bottom of the page.
                  if (isSmallTable(tbl) && isSmallTable(nextTbl)) {
                    return (
                      <View key={`tbl-${idx}`} style={styles.section} wrap={false}>
                        <Text style={styles.sectionTitle}>{key}</Text>
                        {renderPdfTable(tbl, `tbl-${idx}`, undefined, { disableWrapFalse: true })}
                        <View style={{ paddingTop: SECTION_TOP_GAP }}>
                          <Text style={styles.sectionTitle}>{nextKey}</Text>
                          {renderPdfTable(nextTbl, `tbl-${idx}-next`, undefined, { disableWrapFalse: true })}
                        </View>
                      </View>
                    );
                  }
                  return (
                    <View key={`tbl-${idx}`} style={styles.section}>
                      <SplitAnchor />
                      {renderPdfTable(tbl, `tbl-${idx}`, key)}
                      <View style={{ paddingTop: SECTION_TOP_GAP }}>
                        <SplitAnchor />
                        {renderPdfTable(nextTbl, `tbl-${idx}-next`, nextKey)}
                      </View>
                    </View>
                  );
                }

                const small = isSmallTable(tbl);
                // For small tables we wrap the whole section (title + full table) in wrap={false}.
                // For large tables renderPdfTable renders the title inside its own orphan-protected intro block.
                return (
                  <View key={`tbl-${idx}`} style={styles.section} {...(small ? { wrap: false } : {})}>
                    {small ? null : <SplitAnchor />}
                    {small ? <Text style={styles.sectionTitle}>{key}</Text> : null}
                    {renderPdfTable(tbl, `tbl-${idx}`, key, small ? { disableWrapFalse: true } : undefined)}
                </View>
                );
              }
              return null;
            });
            })()
          : null}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
        />
        <Text style={styles.footerLeft} fixed render={() => `${modelDetails?.name ?? "Model Summary"}  |  Powered by underwritRE.com`} />
      </Page>
    </Document>
  );
};

export default PdfSummaryDocument;


